import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { KeywordFilterConfig } from '@new-signal/shared';
import { cn } from '../lib/utils';

const MAX_KEYWORDS = 20;

interface KeywordFilterCardProps {
  config: KeywordFilterConfig;
  onChange: (config: KeywordFilterConfig) => void;
  onRemove: () => void;
  error?: string | null;
}

export function KeywordFilterCard({ config, onChange, onRemove, error }: KeywordFilterCardProps) {
  const { t } = useTranslation('feed-builder');
  const { t: tc } = useTranslation('common');
  const [input, setInput] = useState('');

  const handleAddKeyword = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    if (config.keywords.includes(trimmed)) {
      setInput('');
      return;
    }
    onChange({ ...config, keywords: [...config.keywords, trimmed] });
    setInput('');
  };

  const handleRemoveKeyword = (index: number) => {
    onChange({ ...config, keywords: config.keywords.filter((_, i) => i !== index) });
  };

  const handleModeChange = (mode: 'include' | 'exclude') => {
    onChange({ ...config, mode });
  };

  const atLimit = config.keywords.length >= MAX_KEYWORDS;

  return (
    <div className="mb-4 rounded-xl border border-brand-border bg-brand-canvas p-4">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">&#128269;</span>
          <strong>{t('keywordFilter.title')}</strong>
        </div>
        <button
          onClick={onRemove}
          title={t('keywordFilter.removeAction')}
          className="cursor-pointer border-none bg-transparent px-1 text-lg leading-none text-brand-muted"
        >
          &times;
        </button>
      </div>

      {/* Mode toggle */}
      <div className="mb-3">
        <span className="mr-2 text-sm">{t('keywordFilter.mode')}</span>
        <button
          onClick={() => handleModeChange('include')}
          className={cn(
            'mr-1 cursor-pointer rounded border border-brand-border-medium px-3 py-1',
            config.mode === 'include' ? 'bg-accent-blue text-white' : 'bg-white text-brand-text',
          )}
        >
          {t('keywordFilter.include')}
        </button>
        <button
          onClick={() => handleModeChange('exclude')}
          className={cn(
            'cursor-pointer rounded border border-brand-border-medium px-3 py-1',
            config.mode === 'exclude' ? 'bg-accent-blue text-white' : 'bg-white text-brand-text',
          )}
        >
          {t('keywordFilter.exclude')}
        </button>
      </div>

      {/* Keyword chips */}
      {config.keywords.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {config.keywords.map((kw, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-2xl bg-accent-blue-light px-2.5 py-1 text-[13px]"
            >
              {kw}
              <button
                onClick={() => handleRemoveKeyword(i)}
                className="cursor-pointer border-none bg-transparent p-0 text-sm leading-none text-brand-subtle"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add keyword input */}
      <form onSubmit={handleAddKeyword} className="mb-2 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('keywordFilter.placeholder')}
          disabled={atLimit}
          className="flex-1 rounded border border-brand-border-medium p-2 text-sm"
        />
        <button
          type="submit"
          disabled={atLimit || !input.trim()}
          className="cursor-pointer rounded border border-brand-border-medium bg-white px-4 py-2"
        >
          {tc('add')}
        </button>
      </form>

      {atLimit && (
        <p className="mb-1 text-[13px] text-accent-warning">
          {t('keywordFilter.maxKeywords', { max: MAX_KEYWORDS })}
        </p>
      )}

      {error && (
        <p className="mb-1 text-[13px] text-brand-error">{error}</p>
      )}

      {/* Help text */}
      <p className="mt-1 text-[13px] text-brand-subtle">
        {config.mode === 'include'
          ? t('keywordFilter.includeHelp')
          : t('keywordFilter.excludeHelp')}
      </p>
    </div>
  );
}
